'use client';

import { ArrowLeft, CheckCircle2, CreditCard } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import type { PaymentMethod } from '@/features/payments/domain/model/payment-method';
import { TotemCheckoutSummary } from '@/features/totems/presentation/components/web-totem/checkout/totem-checkout-summary';
import { TotemPaymentMethodPicker } from '@/features/totems/presentation/components/web-totem/checkout/totem-payment-method-picker';
import { useTotemCheckout } from '@/features/totems/presentation/hooks/web-totem/use-totem-checkout';
import {
  useTotemCartState,
  useTotemCatalog,
  useTotemOrder,
} from '@/features/totems/presentation/providers/web-totem/totem-provider';

export function TotemCheckoutDialog() {
  const { totem } = useTotemCatalog();
  const { cart } = useTotemCartState();
  const { checkoutOpen } = useTotemOrder();
  const {
    step,
    availableMethods,
    qrData,
    qrMode,
    amountTendered,
    setAmountTendered,
    handleCashPayment,
    handleQrPayment,
    handleCardPayment,
    handleMpLinkPayment,
    handleClose,
    handleBack,
    isPending,
    cashNumberStr,
  } = useTotemCheckout();

  const handleMethodSelect = (method: PaymentMethod) => {
    if (method.type === 'efectivo') {
      handleCashPayment(method);
    } else if (totem?.web || method.type === 'mercado_pago') {
      // En la carta web móvil, todo cobro de Mercado Pago (sea type qr o mercado_pago)
      // se procesa a través de la preferencia oficial de Mercado Pago (createMpPreference)
      handleMpLinkPayment(method);
    } else if (method.type === 'qr') {
      handleQrPayment(method);
    } else {
      handleCardPayment(method);
    }
  };

  return (
    <Dialog open={checkoutOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent
        className="flex max-h-[95vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden border-0 bg-background/95 p-0 backdrop-blur-md sm:max-w-[95vw] md:max-w-6xl lg:max-w-7xl"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Cobrar pedido</DialogTitle>
        <DialogDescription className="sr-only">Seleccione un método de pago y confirme el pedido.</DialogDescription>

        <div className="flex h-full min-h-[60vh] flex-1 flex-col">
          {/* Custom Header */}
          <div className="flex shrink-0 items-center gap-4 border-b bg-background px-6 py-4">
            <button
              type="button"
              onClick={handleBack}
              className="flex size-10 items-center justify-center rounded-full bg-muted/50 transition-colors hover:bg-muted"
            >
              <ArrowLeft className="size-5" />
            </button>
            <h2 className="text-xl font-bold">
              {step === 'payment' && (totem?.web ? 'Confirmar y pagar' : 'Cobrar pedido')}
              {step === 'processing' && 'Procesando...'}
              {step === 'waiting_terminal' && 'Pago Tarjeta'}
              {step === 'qr_payment' && 'Pago QR'}
              {step === 'cashier' && 'Pago exitoso'}
            </h2>
          </div>

          {step === 'payment' && (
            <div className="grid min-h-0 flex-1 grid-cols-1 divide-y overflow-hidden bg-muted/10 md:grid-cols-12 md:divide-x md:divide-y-0">
              {/* Left Column: Payment Methods */}
              <div className="col-span-1 flex flex-col gap-8 overflow-y-auto p-6 md:col-span-7 md:p-8 lg:col-span-8 lg:p-10">
                <div className="flex-1 rounded-2xl border bg-background p-6 shadow-sm md:p-8">
                  <TotemPaymentMethodPicker
                    isPending={isPending}
                    methods={availableMethods}
                    onSelect={handleMethodSelect}
                    cartTotal={cart.total}
                    amountTendered={amountTendered}
                    onAmountTenderedChange={setAmountTendered}
                    isWebTotem={totem?.web}
                  />
                </div>
              </div>

              {/* Right Column: Summary */}
              <div className="col-span-1 flex min-h-0 flex-col bg-background p-6 md:col-span-5 md:p-8 lg:col-span-4">
                <div className="mb-6 flex min-h-0 flex-1 flex-col">
                  <TotemCheckoutSummary />
                </div>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-6 py-20">
              <Spinner className="size-12" />
              <p className="text-xl font-medium">Estamos creando tu pedido...</p>
            </div>
          )}

          {step === 'waiting_terminal' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 py-12 text-center">
              <div>
                <h3 className="mb-2 text-3xl font-bold">Pagá con tu tarjeta!</h3>
                <p className="text-lg text-muted-foreground">Una vez que el pago sea exitoso, tu pedido se procesará</p>
              </div>

              <div className="relative flex items-center justify-center py-8">
                <div className="absolute size-48 animate-ping rounded-full bg-orange-500/10" />
                <div className="absolute size-32 animate-pulse rounded-full bg-orange-500/20" />
                <div className="relative rotate-[-10deg] transform rounded-2xl border bg-white p-6 shadow-xl transition-transform hover:rotate-0">
                  <CreditCard className="size-20 text-slate-800" />
                </div>
              </div>

              <h4 className="mt-4 max-w-sm text-xl font-bold">
                Acercá tu tarjeta a la terminal para completar el pago!
              </h4>

              <div className="mt-8">
                <p className="text-sm text-muted-foreground">
                  Para cancelar el pago, presioná el botón de cancelar en el lector.
                </p>
              </div>
            </div>
          )}

          {step === 'cashier' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-20 text-center">
              <CheckCircle2 className="size-24 text-emerald-500" />
              <h3 className="text-3xl font-bold">Pedido generado con éxito</h3>
              {cashNumberStr && <div className="my-4 text-6xl font-black text-primary">{cashNumberStr}</div>}
              <p className="max-w-sm text-lg text-muted-foreground">
                Aboná este pedido en la caja para que pase a preparación.
              </p>
            </div>
          )}

          {step === 'qr_payment' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 py-12 text-center">
              <div>
                <h3 className="mb-2 text-2xl font-bold">
                  {qrMode === 'static' ? 'Escaneá el QR fijo del local' : 'Escaneá el código QR'}
                </h3>
                {qrMode === 'static' ? (
                  <p className="text-sm text-muted-foreground">
                    Usá el código QR impreso de este punto de venta para completar el pago.
                  </p>
                ) : null}
              </div>

              {qrMode === 'static' ? (
                <div className="flex size-64 items-center justify-center rounded-2xl border border-dashed bg-muted/20 p-6">
                  <p className="text-sm text-muted-foreground">Esperando escaneo del QR estático…</p>
                </div>
              ) : qrData ? (
                <div className="rounded-2xl border-4 border-muted bg-white p-6">
                  <QRCodeSVG value={qrData} size={256} />
                </div>
              ) : (
                <div className="flex size-64 animate-pulse items-center justify-center rounded-2xl border bg-muted/20">
                  <span className="text-muted-foreground">Generando código...</span>
                </div>
              )}

              <div className="mx-auto flex w-full max-w-xs items-center justify-between rounded-full border border-orange-200 bg-orange-50 px-6 py-3 text-orange-600 shadow-sm dark:border-orange-800/50 dark:bg-orange-900/30 dark:text-orange-400">
                <Spinner className="size-5 shrink-0 bg-orange-600 dark:bg-orange-400" />
                <span className="font-medium">Esperando pago...</span>
                <div className="size-5 shrink-0" />
              </div>
            </div>
          )}

          {step === 'mp_redirect' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-20 text-center">
              <Spinner className="size-12" />
              <h3 className="text-3xl font-bold">Redirigiendo a Mercado Pago...</h3>
              <p className="max-w-sm text-lg text-muted-foreground">
                Serás redirigido de forma segura para completar tu pago.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
